import { type FC } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { HtmlPreview } from './HtmlPreview'
import styles from './CustomWorkflows.module.scss'
import type { OutputRenderer } from './outputTypes'

const RecipientLine: FC<{ label: string; values: string[] }> = ({ label, values }) =>
  values.length > 0 ? (
    <div className={styles.recipientLine}>
      <span className={styles.recipientLabel}>{label}</span>
      <span className={styles.recipientChips}>
        {values.map((value, index) => (
          <span key={`${value}-${index}`} className={styles.recipientChip}>
            {value}
          </span>
        ))}
      </span>
    </div>
  ) : null

const EmailBody: FC<{ htmlContent: string | null; textContent: string | null }> = ({ htmlContent, textContent }) => {
  if (htmlContent) {
    return <HtmlPreview html={htmlContent} />
  }
  if (textContent) {
    return <pre className={styles.outputText}>{textContent}</pre>
  }
  return null
}

export const EmailOutput: OutputRenderer = ({ output }) => {
  if (output.kind !== 'email') {
    return null
  }
  const { subject, htmlContent, textContent, recipients, cc, bcc } = output.value
  return (
    <div className={styles.emailOutput}>
      {subject ? <div className={styles.emailSubject}>{subject}</div> : null}
      <RecipientLine label='To' values={recipients} />
      <RecipientLine label='Cc' values={cc} />
      <RecipientLine label='Bcc' values={bcc} />
      <EmailBody htmlContent={htmlContent} textContent={textContent} />
    </div>
  )
}

export const MarkdownOutput: OutputRenderer = ({ output }) =>
  output.kind === 'markdown' ? (
    <div className={styles.richText}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{output.value}</ReactMarkdown>
    </div>
  ) : null

export const TableOutput: OutputRenderer = ({ output }) => {
  if (output.kind !== 'table') {
    return null
  }
  const { columns, rows } = output.value
  if (rows.length === 0) {
    return <span className={styles.outputEmpty}>—</span>
  }
  return (
    <div className={styles.tableWrapper}>
      <table className={styles.outputTable}>
        {columns.length > 0 ? (
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th key={`${column}-${index}`}>{column}</th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <td key={cellIndex}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const KeyValueOutput: OutputRenderer = ({ output }) => {
  if (output.kind !== 'keyValue') {
    return null
  }
  if (output.value.length === 0) {
    return <span className={styles.outputEmpty}>—</span>
  }
  return (
    <dl className={styles.keyValueGrid}>
      {output.value.map((item, index) => (
        <div className={styles.keyValueRow} key={`${item.label}-${index}`}>
          <dt className={styles.keyValueLabel}>{item.label}</dt>
          <dd className={styles.keyValueValue}>{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

const checklistIconClass = (checked: boolean | null): string => {
  if (checked === null) return 'bi-dot zocks-text-gray-500'
  return checked ? 'bi-check-square-fill zocks-text-primary' : 'bi-square zocks-text-gray-500'
}

export const ChecklistOutput: OutputRenderer = ({ output }) => {
  if (output.kind !== 'checklist') {
    return null
  }
  if (output.value.length === 0) {
    return <span className={styles.outputEmpty}>—</span>
  }
  return (
    <ul className={styles.checklist}>
      {output.value.map((item, index) => (
        <li className={styles.checklistItem} key={`${item.text}-${index}`}>
          <i aria-hidden='true' className={`bi ${checklistIconClass(item.checked)}`} />
          {item.checked !== null ? <span className='visually-hidden'>{item.checked ? 'Completed: ' : 'Not completed: '}</span> : null}
          <span>{item.text}</span>
        </li>
      ))}
    </ul>
  )
}
